import { SCHEDULE_SLOT_TYPE } from '@hedepy/common/enums/SCHEDULE_SLOT_TYPE'
import type { FreeSlot } from '@hedepy/common/schemas/modules/schedule/freeSlot'
import { VisuallyHidden } from 'radix-ui'
import { useMemo } from 'react'

import { dayjs } from '@/legacy-src/lib/dayjs/day'
import type { TherapistType } from '@/legacy-src/types/TherapistStateType'
import { getTranslate } from '@/legacy-src/utilities/i18n'
import { capitalize } from '@/legacy-src/utilities/textUtilities'

import { Button } from '@/src/domains/design-system/components/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/src/domains/design-system/components/Dialog'
import { ClientBookingAvailableSlotTimeSlot } from '@/src/domains/client-booking/components/ClientBookingAvailableSlots/ClientBookingAvailableSlot/ClientBookingAvailableSlotTimeSlot'

import { ClientBookingAvailableSlotRebookItem } from './ClientBookingAvailableSlotRebookItem'

export interface ClientBookingAvailableSlotRebookUIProps {
  slot: FreeSlot
  therapist: TherapistType
  onRebookEvent: () => void
}

export const ClientBookingAvailableSlotRebookUI = ({
  slot,
  therapist,
  onRebookEvent,
}: ClientBookingAvailableSlotRebookUIProps) => {
  const { t } = getTranslate()

  const { date, duration } = useMemo(() => {
    return {
      date: dayjs(slot.startDate),
      duration: dayjs(slot.endDate).diff(slot.startDate, 'minute'),
    }
  }, [slot.startDate, slot.endDate])

  return (
    <Dialog>
      <DialogTrigger asChild>
        <ClientBookingAvailableSlotTimeSlot slot={slot} />
      </DialogTrigger>
      <DialogContent>
        <div className="flex flex-col gap-5">
          <DialogTitle>
            {slot.type === SCHEDULE_SLOT_TYPE.ONLINE
              ? t('component.rebooking.online.title')
              : t('component.rebooking.offline.title')}
          </DialogTitle>
          <dl className="m-0 flex flex-col gap-3">
            <ClientBookingAvailableSlotRebookItem
              title={t('component.rebooking.event.date')}
              icon="calendar-rebrand-basic"
            >
              {capitalize(date.format('dddd, '))}
              {date.format('L')}
            </ClientBookingAvailableSlotRebookItem>
            <ClientBookingAvailableSlotRebookItem title={t('component.rebooking.event.time')} icon="clock-rebrand">
              {date.format('LT')} {'–'} {date.add(duration, 'minute').format('LT')}
            </ClientBookingAvailableSlotRebookItem>
            <ClientBookingAvailableSlotRebookItem
              title={t('component.rebooking.event.type')}
              icon={slot.type === SCHEDULE_SLOT_TYPE.ONLINE ? 'video-therapist' : 'location-rebrand'}
            >
              {slot.type === SCHEDULE_SLOT_TYPE.ONLINE ? (
                <span>{t('component.rebooking.online.desc')}</span>
              ) : (
                <span className="text-primary">
                  <VisuallyHidden.Root>{t('component.rebooking.offline.desc')}:</VisuallyHidden.Root>
                  {therapist.office?.location.address}
                </span>
              )}
            </ClientBookingAvailableSlotRebookItem>
          </dl>
          <div className="flex flex-col gap-3 tablet:flex-row">
            <Button size="medium" className="w-full" onClick={onRebookEvent}>
              {t('component.button.confirm')}
            </Button>
            <DialogClose asChild>
              <Button size="medium" variant="secondary" className="w-full">
                {t('component.button.back')}
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
